<template>
  <div class="rich-body-content" v-html="sanitizedHtml"></div>
</template>

<script>
  import DOMPurify from 'dompurify'
  import { defineComponent } from 'vue'
  import { Conversations } from '@/api/conversations'
  import { ACTION_TYPES as CONVERSATION_ACTION_TYPES } from '@/store/modules/conversations'
  import { getLinkPreview, getActorFilePreview } from '@/api/link-preview'
  import { trackMatomoEvent } from '@/util/analytics'
  import { MATOMO_EVENT_CATEGORIES, MATOMO_EVENT_ACTIONS } from '@/constants/analytics-constants'

  // Our own file URLs, which have their own metadata endpoint (see getActorFilePreview).
  const ACTOR_FILE_PATH = /^\/api\/actors\/([^/]+)\/files\/([^/]+)\/?$/

  // Bare URLs sitting in plain text (older bodies were saved without autolinking).
  const BARE_URL = /https?:\/\/[^\s<>"']+/g

  // Module-level cache so the same URL is only resolved once per page load,
  // no matter how many bodies/components reference it.
  const previewPromises = new Map()

  function memoize (key, loader) {
    if (!previewPromises.has(key)) {
      previewPromises.set(key, loader().catch(() => null))
    }

    return previewPromises.get(key)
  }

  export default defineComponent({
    name: 'RichBodyContent',
    props: {
      html: String,
      // Atlassian-style inline smart links: replace a pasted URL in the text with
      // the page's favicon + title (fetched from /api/link-preview).
      smartLinks: {
        type: Boolean,
        default: false,
      },
    },
    data () {
      return {
        popupEl: null,
        popupTimer: null,
      }
    },
    mounted () {
      setTimeout(() => {
        // Open conversation when clicking a mentioned user
        const actorUserMentions = document.querySelectorAll('.data-mention-type--user');
        const _self = this

        actorUserMentions.forEach(mention => {
          mention.addEventListener('click', function(e) {
            e.preventDefault()
            const element = e.target
            const userId = element.getAttribute('rel')

            Conversations.post({ recipient_id: userId }).then((conversation) => {
              _self.$store.dispatch(CONVERSATION_ACTION_TYPES.FETCH_CONVERSATIONS_LIST)
              _self.$router.push('/profile/conversations/' + conversation.id)
            })
          })
        })

      }, 300)

      this.applySmartLinks()
    },
    beforeUnmount () {
      this.hidePopup()
    },
    watch: {
      sanitizedHtml () {
        this.$nextTick(this.applySmartLinks)
      },
    },
    methods: {
      // Post-process anchors: open external links in a new tab and, when the
      // visible text is just a raw URL, shorten it to the domain for readability.
      // Skips user-mention anchors, which have a click handler (see mounted) that
      // opens a conversation in the same tab.
      processAnchor (node) {
        if (node.tagName !== 'A' || !node.hasAttribute('href')) {
          return
        }

        const href = node.getAttribute('href') || ''
        const isExternal = /^https?:\/\//i.test(href)
        const isMention = node.hasAttribute('data-mention-id') || (node.getAttribute('class') || '').includes('data-mention')

        if (isMention) {
          return
        }

        if (isExternal) {
          node.setAttribute('target', '_blank')
          node.setAttribute('rel', 'noopener noreferrer')
        }

        // A link whose visible text is just its URL (external or an internal path)
        // gets flagged, so applySmartLinks() can turn it into a mini card. Authored
        // link text ("click here") is left alone.
        const text = (node.textContent || '').trim()
        const isBareUrl = /^https?:\/\//i.test(text) || (href.startsWith('/') && text === href)

        if (!isBareUrl) {
          return
        }

        node.setAttribute('data-bare-url', '1')

        // Fallback display until the card is rendered: just the domain, e.g.
        // "https://platform.foodleap.com/files/123" -> "platform.foodleap.com".
        if (isExternal) {
          node.textContent = text.replace(/^https?:\/\//i, '').replace(/^www\./i, '').split('/')[0]
        }
      },
      // Turn every flagged bare URL into an inline mini card — always, even when
      // there's no metadata to be had, so a link never renders as naked text.
      applySmartLinks () {
        if (!this.smartLinks || !this.$el || !this.$el.querySelectorAll) {
          return
        }

        this.linkifyBareUrls()

        this.$el.querySelectorAll('a[data-bare-url]:not([data-smart-link])').forEach(anchor => {
          anchor.setAttribute('data-smart-link', 'pending')

          this.resolvePreview(anchor.getAttribute('href')).then(preview => {
            if (anchor.isConnected) {
              this.renderSmartLink(anchor, preview)
            }
          })
        })
      },
      // Plenty of bodies were saved with the URL as plain text rather than a link
      // (no autolink at the time, or the text was pasted in programmatically). Wrap
      // those in anchors first, so they get a card like any other link.
      linkifyBareUrls () {
        const walker = document.createTreeWalker(this.$el, NodeFilter.SHOW_TEXT, {
          acceptNode: node => {
            BARE_URL.lastIndex = 0

            if (!BARE_URL.test(node.nodeValue) || node.parentElement.closest('a, code, pre')) {
              return NodeFilter.FILTER_REJECT
            }

            return NodeFilter.FILTER_ACCEPT
          },
        })

        // Collected up front: replacing a node while walking would invalidate the walker.
        const nodes = []
        while (walker.nextNode()) {
          nodes.push(walker.currentNode)
        }

        nodes.forEach(this.linkifyTextNode)
      },
      linkifyTextNode (node) {
        const text = node.nodeValue
        const fragment = document.createDocumentFragment()
        let index = 0
        let match

        BARE_URL.lastIndex = 0

        while ((match = BARE_URL.exec(text)) !== null) {
          // Sentence punctuation right after a URL isn't part of it.
          const href = match[0].replace(/[.,;:!?)\]}>]+$/, '')

          fragment.appendChild(document.createTextNode(text.slice(index, match.index)))
          fragment.appendChild(this.buildAnchor(href))

          index = match.index + href.length
          BARE_URL.lastIndex = index
        }

        fragment.appendChild(document.createTextNode(text.slice(index)))
        node.replaceWith(fragment)
      },
      buildAnchor (href) {
        const anchor = document.createElement('a')
        anchor.href = href
        anchor.textContent = href.replace(/^https?:\/\//i, '').replace(/^www\./i, '').split('/')[0]
        anchor.target = '_blank'
        anchor.rel = 'noopener noreferrer'
        anchor.setAttribute('data-bare-url', '1')

        return anchor
      },
      // External URLs go through the unfurler; our own file URLs have a metadata
      // endpoint (they stream bytes, so they can't be unfurled); anything else
      // internal is described from its own URL. Every branch resolves to a usable
      // preview — a bare domain card at worst.
      resolvePreview (href) {
        if (!this.isInternal(href)) {
          return memoize(href, () => getLinkPreview(href))
            .then(preview => (preview && preview.ok ? preview : this.fallbackPreview(href)))
        }

        const file = new URL(href, window.location.origin).pathname.match(ACTOR_FILE_PATH)

        if (!file) {
          return Promise.resolve(this.internalPreview(href))
        }

        return memoize(href, () => getActorFilePreview(file[1], file[2]))
          .then(preview => (preview && preview.ok ? preview : this.internalPreview(href)))
      },
      // Last resort: the domain plus its favicon, so the link still reads as a card.
      fallbackPreview (href) {
        try {
          const url = new URL(href)

          return {
            ok: true,
            url: url.href,
            title: url.host.replace(/^www\./i, ''),
            description: null,
            image: null,
            favicon: `${url.origin}/favicon.ico`,
            site_name: url.host,
            type: 'link',
          }
        } catch (e) {
          return { ok: true, url: href, title: href, description: null, image: null, favicon: null, site_name: null, type: 'link' }
        }
      },
      isInternal (href) {
        if (href.startsWith('/')) {
          return true
        }

        try {
          return new URL(href).host === window.location.host
        } catch (e) {
          return false
        }
      },
      // Build a preview for one of our own pages from the URL alone: the section it
      // points at ("/files/XVpzM086kQ" -> "Files") plus the path as the subtitle.
      internalPreview (href) {
        const url = new URL(href, window.location.origin)
        const section = url.pathname.split('/').filter(segment => segment && segment !== 'api')[0]

        return {
          ok: true,
          url: url.href,
          title: section ? this.humanize(section) : url.host,
          description: decodeURIComponent(url.pathname),
          image: null,
          favicon: `${url.origin}/favicon.ico`,
          site_name: url.host,
          type: 'internal',
        }
      },
      humanize (segment) {
        const words = segment.replace(/[-_]+/g, ' ').trim()

        return words.charAt(0).toUpperCase() + words.slice(1)
      },
      // Renders a one-line mini card: favicon (or file-type badge) + page title.
      renderSmartLink (anchor, preview) {
        anchor.textContent = ''
        anchor.classList.add('smart-link')
        anchor.setAttribute('data-smart-link', 'done')
        anchor.setAttribute('title', preview.title)

        anchor.appendChild(this.smartLinkIcon(preview))

        const title = document.createElement('span')
        title.className = 'smart-link__title'
        title.textContent = preview.title
        anchor.appendChild(title)

        anchor.addEventListener('click', () => {
          trackMatomoEvent(MATOMO_EVENT_CATEGORIES.LINK_PREVIEW, MATOMO_EVENT_ACTIONS.CLICK, preview.url)
        })

        this.attachPopup(anchor, preview)
      },
      // Files and direct images get an extension badge (PDF, XLSX, PNG, …);
      // regular pages get their favicon, falling back to the badge if it 404s.
      smartLinkIcon (preview) {
        const isFile = preview.type === 'file' || preview.type === 'image'

        if (!isFile && preview.favicon) {
          const favicon = document.createElement('img')
          favicon.className = 'smart-link__favicon'
          favicon.src = preview.favicon
          favicon.alt = ''
          favicon.addEventListener('error', () => favicon.replaceWith(this.fileBadge(preview)))

          return favicon
        }

        return this.fileBadge(preview)
      },
      fileBadge (preview) {
        const badge = document.createElement('span')
        badge.className = 'smart-link__badge'
        badge.textContent = this.fileExtension(preview)

        return badge
      },
      fileExtension (preview) {
        const extension = (preview.title || '').match(/\.([a-z0-9]{1,5})$/i)
        if (extension) {
          return extension[1].toUpperCase()
        }

        const mime = preview.mime || ''

        return mime ? mime.split('/').pop().toUpperCase().substring(0, 4) : 'WWW'
      },
      // Hovering a mini card reveals the full preview (image + description) in a
      // floating card. It lives on <body> so the feed's clamped, overflow-hidden
      // container can't clip it.
      attachPopup (anchor, preview) {
        anchor.addEventListener('mouseenter', () => {
          clearTimeout(this.popupTimer)
          this.popupTimer = setTimeout(() => this.showPopup(anchor, preview), 250)
        })
        anchor.addEventListener('mouseleave', this.hidePopup)
        anchor.addEventListener('focus', () => this.showPopup(anchor, preview))
        anchor.addEventListener('blur', this.hidePopup)
      },
      showPopup (anchor, preview) {
        this.hidePopup()

        if (!anchor.isConnected) {
          return
        }

        this.popupEl = this.buildPopup(preview)
        document.body.appendChild(this.popupEl)
        this.positionPopup(this.popupEl, anchor)

        // Any scroll (including inside scrollable panels) invalidates the position.
        window.addEventListener('scroll', this.hidePopup, true)
      },
      hidePopup () {
        clearTimeout(this.popupTimer)

        if (this.popupEl) {
          this.popupEl.remove()
          this.popupEl = null
          window.removeEventListener('scroll', this.hidePopup, true)
        }
      },
      buildPopup (preview) {
        const popup = document.createElement('div')
        popup.className = 'smart-link-popup'

        if (preview.image) {
          const image = document.createElement('img')
          image.className = 'smart-link-popup__image'
          image.src = preview.image
          image.alt = ''
          // A dead image URL would leave an empty grey block — drop it instead.
          image.addEventListener('error', () => image.remove())
          popup.appendChild(image)
        }

        const body = document.createElement('div')
        body.className = 'smart-link-popup__body'

        const title = document.createElement('div')
        title.className = 'smart-link-popup__title'
        title.textContent = preview.title
        body.appendChild(title)

        if (preview.description) {
          const description = document.createElement('div')
          description.className = 'smart-link-popup__description'
          description.textContent = preview.description
          body.appendChild(description)
        }

        const meta = document.createElement('div')
        meta.className = 'smart-link-popup__meta'
        meta.appendChild(this.smartLinkIcon(preview))

        const host = document.createElement('span')
        host.textContent = preview.site_name || this.hostOf(preview.url)
        meta.appendChild(host)
        body.appendChild(meta)

        popup.appendChild(body)

        return popup
      },
      positionPopup (popup, anchor) {
        const margin = 8
        const rect = anchor.getBoundingClientRect()
        const { offsetWidth: width, offsetHeight: height } = popup

        const fitsBelow = rect.bottom + margin + height <= window.innerHeight
        const top = fitsBelow ? rect.bottom + margin : Math.max(margin, rect.top - height - margin)
        const left = Math.max(margin, Math.min(rect.left, window.innerWidth - width - margin))

        popup.style.top = `${top}px`
        popup.style.left = `${left}px`
      },
      hostOf (url) {
        try {
          return new URL(url).host
        } catch (e) {
          return url
        }
      },
    },
    computed: {
      sanitizedHtml () {
        DOMPurify.addHook('afterSanitizeAttributes', this.processAnchor)

        // Allow HTML attributes for parsed elements in rich body content
        const clean = DOMPurify.sanitize(this.html, { ADD_TAGS: ['iframe', 'a'], ALLOWED_ATTR: ['src', 'frameborder', 'allowfullscreen', 'href', 'target', 'data-mention-id', 'data-bare-url', 'rel', 'class'] })

        // Scope the hook to this sanitize call so it doesn't stack across renders/components
        DOMPurify.removeHook('afterSanitizeAttributes')

        return clean
      },
    },
  })
</script>

<style lang="scss">
  .rich-body-content {
    font-size: 0.875rem;
    margin-bottom: 3rem;

    a {
      line-break: anywhere;
    }

    // Indicate links that open in a new tab with a small external-link icon.
    a[target="_blank"]::after {
      content: "";
      display: inline-block;
      width: 0.7em;
      height: 0.7em;
      margin-left: 0.15em;
      vertical-align: -0.02em;
      background-color: currentColor;
      -webkit-mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6'/%3E%3Cpolyline points='15 3 21 3 21 9'/%3E%3Cline x1='10' y1='14' x2='21' y2='3'/%3E%3C/svg%3E") no-repeat center / contain;
      mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6'/%3E%3Cpolyline points='15 3 21 3 21 9'/%3E%3Cline x1='10' y1='14' x2='21' y2='3'/%3E%3C/svg%3E") no-repeat center / contain;
    }

    // Inline smart link: a one-line mini card rendered in place of a pasted URL,
    // with the site favicon (or a file-type badge) in front of the page title.
    a.smart-link {
      display: inline-flex;
      align-items: center;
      gap: 0.35em;
      max-width: 100%;
      vertical-align: baseline;
      line-break: normal;
      padding: 0.05em 0.45em;
      border: 1px solid #e2e5e9;
      border-radius: 4px;
      background: #fff;
      color: inherit;
      text-decoration: none;
      transition: border-color 0.15s ease, background-color 0.15s ease;

      &:hover {
        border-color: #c3c9d1;
        background: #f7f8fa;
        text-decoration: none;
      }

      // Keep the new-tab indicator, but never let it wrap away from the title.
      &::after {
        flex: 0 0 auto;
        opacity: 0.6;
      }
    }

    // One line only — long titles are ellipsised rather than wrapped.
    .smart-link__title {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    p,
    ul,
    ol {
      font-size: inherit;
      margin-bottom: 1rem;
    }

    ul,
    ol {
      margin-left: 1.5rem;
    }

    img,
    iframe {
      display: flex;
      justify-content: center;
      margin: auto;
    }

    iframe {
      height: 340px;
      width: 560px;
    }

    ul li {
      list-style-type: disc;
    }

    ol li {
      list-style-type: decimal;
    }
  }

  // Shared by the inline mini card and the hover popup. Listed for both contexts
  // so the icon keeps its inline sizing inside .rich-body-content (which styles
  // every other img as a centred block) and on the body-level popup.
  .rich-body-content .smart-link__favicon,
  .smart-link-popup .smart-link__favicon {
    flex: 0 0 auto;
    display: inline-block;
    width: 1em;
    height: 1em;
    margin: 0;
    object-fit: contain;
    border-radius: 2px;
  }

  .rich-body-content .smart-link__badge,
  .smart-link-popup .smart-link__badge {
    flex: 0 0 auto;
    padding: 0 0.25em;
    border-radius: 2px;
    background: #eef1f4;
    color: #5a6572;
    font-size: 0.7em;
    font-weight: 700;
    letter-spacing: 0.02em;
  }

  // Hover preview for a smart link. Appended to <body>, positioned in JS.
  .smart-link-popup {
    position: fixed;
    z-index: 1100;
    width: 320px;
    max-width: calc(100vw - 16px);
    overflow: hidden;
    border: 1px solid #e2e5e9;
    border-radius: 8px;
    background: #fff;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
    pointer-events: none;
    font-size: 0.875rem;

    &__image {
      display: block;
      width: 100%;
      height: 150px;
      object-fit: cover;
      background: #f2f4f6;
    }

    &__body {
      padding: 0.6rem 0.75rem;
    }

    &__title {
      font-weight: 600;
      font-size: 0.9em;
      line-height: 1.3;
      color: #1f2733;
      overflow: hidden;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
    }

    &__description {
      margin-top: 0.25rem;
      font-size: 0.82em;
      line-height: 1.35;
      color: #5a6572;
      overflow: hidden;
      display: -webkit-box;
      -webkit-line-clamp: 3;
      -webkit-box-orient: vertical;
    }

    &__meta {
      display: flex;
      align-items: center;
      gap: 0.35rem;
      margin-top: 0.45rem;
      font-size: 0.74em;
      color: #8a94a0;
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
    }
  }
</style>
